import React from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Stack, 
  IconButton, 
  Divider, 
  CircularProgress,
  Tooltip
} from '@mui/material';
import { 
  Close, 
  SwapHoriz, 
  MergeType 
} from '@mui/icons-material';
import { BottomFloatingPanel } from '../../primitives';
import { TagChipBare } from './CRMTags';
import { TagsMergeData } from '../data/useTagsMerge';

interface MergePanelProps {
  merge: TagsMergeData;
  onComplete?: () => void;
}

export const MergeTagsPanel = ({ merge, onComplete }: MergePanelProps) => {
  if(!merge.isPanelVisible) {
    return null;
  }

  return (
    <BottomFloatingPanel>
      <Box sx={{ width: '100%', minWidth: 350, maxWidth: 800, margin: '0 auto' }}>
        
        {/* HEADER */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0}>
          <Typography variant="h6" color="textSecondary" display="flex" alignItems="center" gap={1}>
            <MergeType color="primary" /> Слияние тегов
          </Typography>
          <IconButton onClick={merge.cancelMerge} size="small" disabled={merge.isMerging}>
            <Close />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={3}>
          
          {/* TARGET TAG */}
          <Box>
            <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
              Целевой (останется)
            </Typography>
            <Stack alignItems={"flex-start"} sx={{ mt: 1 }}>
              {merge.targetTag && (
                <TagChipBare tag={merge.targetTag} />
              )}
            </Stack>
          </Box>

          {/* SOURCE TAGS */}
          <Box>
            <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
              Присоединяемые (будут удалены)
            </Typography>
            
            <Stack spacing={1} sx={{ mt: 1 }} alignItems={"flex-start"}>
              {merge.sourceTags.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', py: 1 }}>
                  Выберите теги в списке, чтобы добавить их для слияния.
                </Typography>
              ) : (
                merge.sourceTags.map(source => (
                  <Box 
                    key={source._id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                    }}
                  >
                    <TagChipBare tag={source} />
                    
                    <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
                      <Tooltip title="сделать целевым">
                        <IconButton 
                          size="small" 
                          color="default" 
                          onClick={() => merge.makeTarget(source)}
                          disabled={merge.isMerging}
                          >
                          <SwapHoriz fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => merge.removeSource(source._id)}
                        disabled={merge.isMerging}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                ))
              )}
            </Stack>
          </Box>

        </Stack>

        <Divider sx={{ mt: 3, mb: 2 }} />

        {/* ACTION BUTTONS */}
        <Box display="flex" justifyContent="flex-end" alignItems="center">
          <Button 
            variant="contained" 
            color="primary" 
            size="small"
            disabled={!merge.canSubmitMerge || merge.isMerging}
            onClick={async () =>{
               await merge.executeMerge()
               if(onComplete) {
                onComplete();
               }
              }}
            startIcon={merge.isMerging ? <CircularProgress size={20} color="inherit" /> : <MergeType />}
          >
            {merge.isMerging ? 'Слияние...' : 'Выполнить слияние'}
          </Button>
        </Box>
      </Box>
    </BottomFloatingPanel>
  );
};